// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect, useState } from 'react';
import { Row, Col } from 'antd/lib/grid';
import { Button, Input, Space, Table } from 'antd';
import Text from 'antd/lib/typography/Text';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import UserSublist from './user-sublist';
import UserSelector, { User } from './user-selector';
import { Progress } from 'antd';
import Spin from 'antd/lib/spin';

interface Props {
    jobs: any[];
    users: any[];
    submissions: any[];
    fetching: boolean;
}

function UserListComponent(props: Props): JSX.Element {
    const [data, setData] = useState<ColumnType[]>([]);
    const [filteredUser, setFilteredUser] = useState<any>(null);

    interface ColumnType {
        key: React.Key;
        username: string;
        userId: number;
        jobCount: number;
        inProgress: number;
        completionProgress: number;
    }

    type DataIndex = keyof ColumnType;

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<ColumnType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <UserSelector
                    className='cvat-job-assignee-selector'
                    value={filteredUser}
                    onSelect={(value: User | null): void => {
                        setFilteredUser(value);
                        close();
                    }}
                />
                <br />
                <Button
                    onClick={() => {
                        setFilteredUser(null);
                        close();
                    }}
                    size='small'
                    style={{ width: 90, marginTop: 5 }}
                >
                    Reset
                </Button>
            </div>
        ),
        filterIcon: () => <SearchOutlined style={{ color: filteredUser ? '#1677ff' : undefined }} />,
    });

    const columns: TableColumnsType<ColumnType> = [
        {
            title: 'User',
            dataIndex: 'username',
            key: 'username',
            ...getColumnSearchProps('username'),
        },
        {
            title: 'User ID',
            dataIndex: 'userId',
            key: 'userId',
            sorter: (a: ColumnType, b: ColumnType) => a.userId - b.userId,
        },
        {
            title: '# Assigned Jobs',
            dataIndex: 'jobCount',
            key: 'jobCount',
            defaultSortOrder: 'descend',
            sorter: (a: ColumnType, b: ColumnType) => a.jobCount - b.jobCount,
        },
        {
            title: '# In Progress Jobs',
            dataIndex: 'inProgressJobs',
            key: 'inProgressJobs',
        },
        {
            title: 'In Progress',
            dataIndex: 'inProgress',
            key: 'inProgress',
            render: (inProgress: number) => <Progress percent={inProgress} size='small' />,
            sorter: (a: ColumnType, b: ColumnType) => a.inProgress - b.inProgress,
        },
        {
            title: 'Jobs Completed',
            dataIndex: 'completionProgress',
            key: 'completionProgress',
            render: (completionProgress: number) => <Progress percent={completionProgress} size='small' />,
            sorter: (a: ColumnType, b: ColumnType) => a.completionProgress - b.completionProgress,
        },
    ];

    useEffect(() => {
        const { users, jobs, submissions } = props;

        const getUserData = (user: any) => {
            const userJobs = jobs.filter((job) => job.assignee?.id === user.id);
            const userSubmissions = submissions.filter((submission) => submission.user_id === user.id);
            const uniqueUserSubmissions = userSubmissions.filter(
                (submission, index, self) =>
                    index === self.findIndex((t) => t.job_id === submission.job_id && t.phase === submission.phase),
            );
            const totalJobs = userJobs.length + uniqueUserSubmissions.length;
            const completionProgress = totalJobs ? (uniqueUserSubmissions.length / totalJobs) * 100 : 0;
            const inProgressJobs = userJobs.filter((job) => job.state === 'in progress').length;
            const inProgress = userJobs.length ? (inProgressJobs / userJobs.length) * 100 : 100;

            return {
                key: user.id,
                username: user.username,
                userId: user.id,
                jobCount: userJobs.length,
                inProgressJobs: inProgressJobs,
                inProgress: Math.round(inProgress),
                completionProgress: Math.round(completionProgress),
            };
        };

        if (filteredUser === null) {
            const usersData = users.map(getUserData);
            setData(usersData);
        } else {
            const user = users.find((user) => user.id === filteredUser.id);
            if (user) {
                const userData = getUserData(user);
                setData([userData]);
            }
        }
    }, [filteredUser, props.users, props.jobs, props.submissions]);

    return (
        <div className='cvat-task-job-list'>
            <Row justify='space-between' align='middle' style={{ marginBottom: 10 }}>
                <Col>
                    <Text className='cvat-text-color cvat-jobs-header'> Users </Text>
                </Col>
                <Col>
                    <Text className='cvat-text-color'>{`${data.length} Users`}</Text>
                </Col>
            </Row>
            <Table
                className='cvat-task-jobs-table'
                rowClassName={() => 'cvat-task-jobs-table-row'}
                columns={columns}
                dataSource={data}
                size='small'
                loading={props.fetching}
                loadingIndicator={<Spin size='large' className='cvat-spinner' />}
                expandable={{
                    expandedRowRender: (record: ColumnType) => (
                        <UserSublist jobs={props.jobs} userID={record.userId} submissions={props.submissions} />
                    ),
                    rowExpandable: (record: ColumnType) => record.jobCount > 0 || record.completionProgress > 0,
                }}
            />
        </div>
    );
}

export default React.memo(UserListComponent);
